'use client';

/**
 * FavouritesGridCard — the canvas home for the buyer's favourites.
 *
 * Self-fetches `saved_list` (the caller's SAVED set) and paints the SAME
 * `ProductTile` grid that browsing a vendor or searching paints. Favourites is
 * a CROSS-vendor set, so tiles show the vendor line (showVendor). Clicking a
 * tile INSPECTS the product (rail brief) without changing the canvas selection —
 * the `favourites` explorer-selection owns the canvas, so selectNode would be
 * masked (mirrors product_search_results exactly).
 *
 * Painted on the mode4_favourites preset (sel:favourites), which the "My
 * Favourites" explorer grouping row sets on click.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="favourites_grid"
 * @see components/canvas/cards/vendors/product-tile.tsx — the shared tile
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { Loader2, Heart } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductSummary, SavedProductRow } from '@/lib/api/types';
import { ProductTile } from '../vendors/product-tile';
import { CardGrid } from '../card-grid';

interface SavedListResponse {
  products: SavedProductRow[];
  count: number;
}

/** A saved row → the tile's ProductSummary. saved=true by definition (these ARE
 *  the favourites), so the ♥ paints filled. */
function toSummary(row: SavedProductRow): ProductSummary {
  return {
    semantic_id: row.product_sid,
    name: row.name,
    list_price: row.list_price ?? null,
    qty_available: null,
    image_url: row.image_url ?? null,
    saved: true,
  };
}

export function FavouritesGridCard() {
  // Layer-1 chrome (server-driven).
  const title = useProductLabel('favourites_group');
  const emptyLabel = useProductLabel('favourites_empty');

  const strategy = useCapabilityStore((s) => s.strategyName('products', 'saved_list'));
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const inspectedEntity = useGraphSelectionStore((s) => s.inspectedEntity);

  const { data, isLoading } = useStrategyData<SavedListResponse>(strategy, {}, !!strategy);
  const res = data?.data;
  const rows = res?.products ?? [];

  // Matches useStrategyData's key shape (['strategy-data', strategy, params]) so
  // the capture envelope pairs this card's scene with its payload.
  const queryKey = useMemo(() => ['strategy-data', strategy, {}] as const, [strategy]);

  useCardExport(useMemo(() => ({
    label: title,
    content_key: 'favourites_grid',
    surface: 'canvas' as const,
    status: (res ? 'loaded' : 'loading') as 'loading' | 'loaded',
    query_key: queryKey,
    text: rows.map((r) => r.name).join('\n'),
    json: {
      count: rows.length,
      products: rows.map((r) => ({
        product_id: r.product_sid, name: r.name, vendor: r.vendor_name ?? null,
      })),
    },
  }), [rows, res, queryKey, title]));

  if (isLoading && !res) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'flex items-center gap-2 px-3 py-3')}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{ fontSize: surfaceBase.canvas }}
        className="flex flex-col items-center justify-center gap-2 py-20 text-center"
      >
        <Heart className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className={cn(text.muted)}>{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="p-3 space-y-3">
      <div className={cn(text.heading, 'flex items-center gap-1.5 px-1')}>
        <Heart className="h-4 w-4 text-rose-400 fill-rose-400" />
        {title}
        <span className={cn(text.muted, 'ml-1')}>{rows.length}</span>
      </div>
      <CardGrid>
        {rows.map((row) => (
          <CardPlacementProvider
            key={row.product_sid}
            value={{ surface: 'canvas', slot_id: `favourite:${row.product_sid}` }}
          >
            <ProductTile
              product={toSummary(row)}
              vendorId={row.vendor_sid ?? ''}
              vendorName={row.vendor_name ?? ''}
              showVendor
              queryKey={queryKey}
              collectionName=""
              onClick={() =>
                inspectEntity({ id: row.product_sid, label: row.name, type: 'product' })
              }
              isSelected={inspectedEntity?.id === row.product_sid}
            />
          </CardPlacementProvider>
        ))}
      </CardGrid>
    </div>
  );
}
